/**
 * a spotlight card for use in product rails as a mobile card
 *
 * @module views/components/SpotlightCard
 * @memberof -Common
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { SpotlightCard as PalSpotlightCard } from 'web-palette/dist/components/SpotlightCard/SpotlightCard';

import Button from '@ulta/core/components/Button/Button';
import Image from '@ulta/core/components/Image/Image';
import Link from '@ulta/core/components/Link_Huge/Link_Huge';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import useLoader from '@ulta/core/hooks/useLoader/useLoader';
import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { constants } from '@ulta/core/utils/constants/constants';
import { handleRedirect } from '@ulta/core/utils/handleLocation/handleLocation';

import * as utils from './SpotlightCard';

/**
 * Represents a SpotlightCard component
 *
 * @method
 * @param { SpotlightCardProps} props - React properties passed from composition
 * @returns SpotlightCard
 */
export const SpotlightCard = function( props ){
  const {
    formattedGraphicElement,
    invokeMutation,
    onDataCaptureEvent,
    onIntersectionEvent,
    product,
    root = defaultProps.root,
    rootMargin = defaultProps.rootMargin,
    spotlight
  } = props;

  if( !spotlight || !product ){
    return null;
  }

  const productRef = useRef();
  const hasFiredDCEvent = useRef( false );
  const [isButtonClicked, setIsButtonClicked] = useState( false );
  const [loader, showLoader, hideLoader] = useLoader( { loading: false, isPageLoader: true, color: 'orange-400' } );
  const { isOverlay } = useOverlay();
  const {
    skuId: spotlightSkuID,
    productId: spotlightProductID,
    viewOptionAction,
    addToBagAction
  } = product;
  let formattedFooterAction = null;
  // Handles last scrolled threshold
  const { intersectionRatio, hasIntersected } = useIntersectionObserver( productRef, {
    root: root,
    rootMargin: rootMargin,
    threshold: [0, 0.5, 1.0]
  } );

  const atbAction = {
    ...addToBagAction,
    variables :{
      moduleParams: {
        skuId: spotlightSkuID,
        productId: spotlightProductID
      }
    }
  };

  const isAddOverlay = isOverlay( atbAction.navigationType );
  if( !isAddOverlay ){
    atbAction.variables.moduleParams.quantity = 1;
  }

  useEffect( () => {
    if( intersectionRatio === 1 ){
      utils.onIntersection( { skuId: spotlightSkuID }, { onIntersectionEvent } );
    }
    else if( !hasFiredDCEvent.current && hasIntersected === true && intersectionRatio >= 0.5 ){
      hasFiredDCEvent.current = true;
      utils.onDataCapture( { skuId: spotlightSkuID }, { onDataCaptureEvent } );
    }
  }, [hasIntersected, intersectionRatio, onDataCaptureEvent, onIntersectionEvent, spotlightSkuID] );

  // Compose action.after function for ATB action
  const handleActionAfter = useCallback(
    utils.composeActionAfter( { hideLoader } ), [hideLoader]
  );

  // Compose onClick for Product Card ATB
  // -> handles invoking the action from DXL
  const handleOnClick = useCallback(
    utils.composeOnClick(
      { spotlightProductID, spotlightSkuID, isAddOverlay, atbAction },
      { invokeMutation, showLoader, handleActionAfter }
    ),
    [invokeMutation, spotlightProductID, spotlightSkuID, isAddOverlay, atbAction, showLoader, handleActionAfter]
  );

  // action to make the card fully clickable
  const formattedFullClickAction = (
    <Link
      variant='absoluteLink'
      { ...product.action }
    />
  );

  // product image. Palette is hooked up to also handle the image on the palette side, but web-site image has the business logic (loading, handling params, etc.). Palette will prioritize the image from web-site.
  const formattedProductImageElement = (
    <Image
      src={ product.image?.imageUrl }
      alt={ product.productName }
    />
  );

  if( viewOptionAction ){
    formattedFooterAction = (
      <Button
        ariaHiddenIcon={ true }
        size='tiny'
        variant='primary'
        { ...viewOptionAction }
        onClick={ ( e ) => {
          e.stopPropagation();
          e.preventDefault();
          handleRedirect( { url: viewOptionAction.url } );
        } }
      />
    );
  }
  else if( addToBagAction ){
    formattedFooterAction = (
      <Button
        ariaHiddenIcon={ true }
        size='tiny'
        variant='primary'
        label={ atbAction.label }
        onClick={ handleOnClick }
        action={ atbAction }
      />
    );
  }

  return (
    <PalSpotlightCard
      className='SpotlightCard'
      product={ product }
      spotlight={ spotlight }
      externalActionFullyClickElement={ formattedFullClickAction }
      externalFooterActionElement={ formattedFooterAction }
      externalGraphicImage={ formattedGraphicElement }
      externalProductImageElement={ formattedProductImageElement }
      showAddToBagButton={ true } // this can be updated to be passed from the product/DXL
    />
  );
};

/**
   * Method to handle action.after callback for atbAction
   * @param {object} methods methods
   * @param {action} methods.closeOverlay utility from useOverlay to close overlay
   * @param {action} methods.hideLoader utility from useLoader to hide loader
   * @param {action} inputData args
   */
export const composeActionAfter = ( methods ) => () => {
  const { hideLoader } = methods || {};

  if( !hideLoader ){
    return;
  }

  hideLoader();
};

/**
   * Method to handle invoking add to bag from product card actions
   * @param {object} methods methods
   * @param {action} methods.invokeMutation handler from layerhost to invoke DXL mutation
   * @param {string} action DXL action
   */
export const composeOnClick = ( data, methods ) => ( ) => {
  const { spotlightSkuID, spotlightProductID, isAddOverlay, atbAction } = data || {};
  const { invokeMutation, showLoader, handleActionAfter } = methods || {};
  const { graphql, customHeaders, params, variables } = atbAction || {};

  if( !invokeMutation || !spotlightSkuID || !spotlightProductID || !atbAction || !showLoader || isAddOverlay ){
    return;
  }

  showLoader();

  const mutation = {
    after: handleActionAfter,
    graphql,
    customHeaders,
    params,
    variables
  };

  invokeMutation( mutation );
};

/**
 * Method to handle on onIntersection event
 * @param {object} data
 * @param {function} onIntersectionEvent
 */
export const onIntersection = ( data, methods ) => {
  const { skuId } = data || {};
  const { onIntersectionEvent } = methods || {};
  if( skuId && onIntersectionEvent ){
    onIntersectionEvent( skuId );
  }
};

/**
 * Method to handle onDataCapture event
 * @param {object} data
 * @param {function} onDataCaptureEvent
 */
export const onDataCapture = ( data, methods ) => {
  const { skuId } = data || {};
  const { onDataCaptureEvent } = methods || {};
  if( skuId && onDataCaptureEvent ){
    onDataCaptureEvent( skuId );
  }
};

/**
 * Property type definitions
 * @typedef SpotlightCardProps
 * @type {object}
 * @property {object} product - product data object
 * @property {object} spotlight - spotlight data object
 */
export const propTypes =  {
  product: PropTypes.object,
  spotlight: PropTypes.object
};

/**
 * Default values for passed properties
 * @type {object}
 */
export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

SpotlightCard.propTypes = propTypes;
SpotlightCard.defaultProps = defaultProps;

export default SpotlightCard;